<template>
  <v-card class="mb-4 pa-2 card-container">
    <v-card-title class="pt-4 card-title">
      <v-container class="pa-0 justify-space-between pr-5" fluid fill-height>
        <span class="pa-1 title">Cadastros</span>
        <chart-type-toggle-button :type.sync="chartType" />
      </v-container>
      <v-card-subtitle class="pa-0">
        <span class="pa-1 subtitle mf-subtitle-1 local-subtitle"> Número de cadastros realizados no aplicativo, até 1 dia antes do atual. </span>
      </v-card-subtitle>
    </v-card-title>
    <mf-loading loader="faturamento" loader-type="chart">
      <apex-chart v-if="series[0].data.length > 0" class="pr-5 pl-5" type="bar" height="350" :options="chartOptions" :series="series"></apex-chart>
      <chart-navigator
        v-if="series[0].data.length > 0"
        :selectedPeriod="chartPeriod"
        :chart-index="chartIndex"
        :metrics-length="metricsLength"
        :metrics-period="metricsPeriod"
        @navigateTo="navigateTo"
      />
      <empty-chart v-if="series[0].data.length === 0" />
    </mf-loading>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { QUERY_GET_APP_NEW_USERS_REGISTERS } from '../../graphql'
import { colors } from '@/config/colors.js'
import ChartTypeToggleButton from '@/components/ChartTypeToggleButton.vue'
import ChartNavigator from '@/components/ChartNavigator'
import EmptyChart from '@/modules/mobile/desempenho/views/components/EmptyChart'
import getAppMetricsNavigatorPeriod from '@/services/getAppMetricsNavigatorPeriod'
import moment from 'moment'

export default {
  components: {
    ChartNavigator,
    ApexChart: VueApexCharts,
    ChartTypeToggleButton,
    EmptyChart,
  },
  props: {
    metricsPeriod: String,
  },
  data: () => ({
    chartType: 'bar',
    chartIndex: 0,
  }),
  computed: {
    series() {
      if (!this.appNewUserRegisters || this.appNewUserRegisters?.length === 0) {
        return [
          {
            data: [],
          },
        ]
      }
      const currentSelectedMetrics = [...this.appNewUserRegisters[this.chartIndex].metrics].reverse()
      const series = this.chartRange.reduce(
        (acc, date) => {
          const [metric] = currentSelectedMetrics.filter((metric) =>
            this.metricsPeriod === 'MONTHLY'
              ? parseInt(moment(metric.date).format('DD')) === date
              : moment(metric.month).locale('pt-br').format('MMMM') === date.toLowerCase()
          )
          if (metric !== undefined) {
            acc[0].data.push(metric.registers_count)
          } else if (
            metric === undefined &&
            !(
              this.chartIndex === 0 &&
              (this.metricsPeriod === 'MONTHLY' ? date > new Date().getDate() : moment(date, 'MMMM', 'pt-br').month() > moment().month())
            )
          ) {
            acc[0].data.push(0)
          } else if (
            metric === undefined &&
            this.chartIndex === 0 &&
            (this.metricsPeriod === 'MONTHLY' ? date > new Date().getDate() : moment(date, 'MMMM', 'pt-br').month() > moment().month())
          ) {
            acc[0].data.push(null)
          }
          return acc
        },
        [
          {
            data: [],
          },
        ]
      )
      return series
    },
    metricsLength() {
      if (!this.appNewUserRegisters) return 0
      return this.appNewUserRegisters.length - 1
    },
    chartPeriod() {
      if (!this.appNewUserRegisters || this.appNewUserRegisters?.length === 0) return
      const currentPeriod = this.metricsPeriod === 'MONTHLY' ? this.appNewUserRegisters[this.chartIndex].month : this.appNewUserRegisters[this.chartIndex].year
      const { navigatorPeriod } = getAppMetricsNavigatorPeriod(this.metricsPeriod, currentPeriod)
      return navigatorPeriod
    },
    chartMax() {
      const currentPeriod =
        this.metricsPeriod === 'MONTHLY' ? this.appNewUserRegisters[this.chartIndex].metrics[0].date : this.appNewUserRegisters[this.chartIndex].year
      const { chartMax } = getAppMetricsNavigatorPeriod(this.metricsPeriod, currentPeriod)
      return chartMax
    },
    chartRange() {
      let i
      let chartRange = []
      if (this.metricsPeriod === 'YEARLY') {
        chartRange = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
      } else {
        for (i = 1; i <= this.chartMax; i++) {
          chartRange.push(i)
        }
      }
      return chartRange
    },
    chartOptions() {
      return {
        chart: {
          height: 350,
          type: this.chartType,
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: false,
          },
          background: '#fff',
        },
        fill: {
          opacity: 1,
          type: 'solid',
        },
        grid: {
          padding: {
            top: 10,
            right: 20,
            bottom: 10,
            left: 20,
          },
        },
        plotOptions: {
          bar: {
            columnWidth: this.metricsPeriod === 'MONTHLY' ? '50px' : '20px',
            borderRadius: 3,
            dataLabels: {
              position: 'top',
            },
          },
        },
        colors: [colors[0]],
        legend: {
          horizontalAlign: 'left',
          markers: {
            width: 7,
            height: 7,
          },
          offsetX: 5,
          offsetY: -10,
        },
        markers: {
          size: 2,
          colors: colors[0],
          strokeColors: colors[0],
          strokeWidth: 2,
          strokeOpacity: 1,
          strokeDashArray: 0,
          fillOpacity: 1,
          shape: 'circle',
          radius: 2,
          showNullDataPoints: true,
          hover: {
            sizeOffset: 2,
          },
        },
        tooltip: {
          custom: function ({ series, seriesIndex, dataPointIndex, w }) {
            const registers = series[0][dataPointIndex]

            return `
              <v-card class="d-flex flex-row justify-start pa-1">
                <span><strong>${registers.toLocaleString()} </strong>${registers > 1 || registers === 0 ? 'cadastros' : 'cadastro'}</span>
              </v-card>
            `
          },
        },
        dataLabels: {
          enabled: true,
          offsetY: this.chartType === 'bar' ? -20 : -10,
          style: {
            fontSize: '12px',
            colors: ['#F9FAFB'],
            fontWeight: 'normal',
          },
          background: {
            enabled: true,
            foreColor: '#969EA7',
            padding: 4,
            borderRadius: 2,
          },
          formatter(val) {
            const formattedValue = val === null ? '' : val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val
            return formattedValue
          },
        },
        stroke: {
          curve: 'smooth',
          colors: [colors[0]],
          width: 3,
        },
        xaxis: {
          categories: this.chartRange,
          category: 'numeric',
          labels: {
            style: {
              colors: '#969EA7',
            },
          },
        },
        yaxis: {
          labels: {
            formatter: function (val, index) {
              if (!val) return
              return val.toLocaleString()
            },
          },
        },
        states: {
          hover: {
            filter: {
              type: 'lighten',
              value: 0.1,
            },
          },
        },
      }
    },
  },
  apollo: {
    appNewUserRegisters: {
      query: QUERY_GET_APP_NEW_USERS_REGISTERS,
      variables() {
        return { metricsPeriod: this.metricsPeriod }
      },
    },
  },
  watch: {
    metricsPeriod() {
      this.chartIndex = 0
    },
  },
  methods: {
    navigateTo(newIndex) {
      this.chartIndex = newIndex
    },
  },
}
</script>

<style lang="scss" scoped>
.card-container {
  min-height: 400px;
}
.local-subtitle {
  width: 80%;
}
</style>
